import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes } from '@mui/system';

const FACES = [
  { name:'Front',  color:'#E61D50', label:'Main Edit'  },
  { name:'Right',  color:'#818cf8', label:'Alt Edit'   },
  { name:'Back',   color:'#34d399', label:'Extras'     },
  { name:'Left',   color:'#fb923c', label:'B-Roll'     },
  { name:'Top',    color:'#f59e0b', label:'Highlights' },
  { name:'Bottom', color:'#a78bfa', label:'Archive'    },
];
const Y_CYCLE = [0,1,2,3]; // Front→Right→Back→Left
const SNAPS   = [
  {rx:-20,ry:0},{rx:-20,ry:-90},{rx:-20,ry:180},{rx:-20,ry:90},{rx:90,ry:0},{rx:-90,ry:0},
];
const SENT = { positive:'#22c55e', negative:'#E61D50', neutral:'#6b7280' };
const float = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}`;
const ring  = keyframes`0%,100%{box-shadow:0 0 0 2px #fff}50%{box-shadow:0 0 0 4px #fff,0 0 14px rgba(255,255,255,.5)}`;

const faceXf = (name,half) => ({
  Front:`translateZ(${half}px)`,Back:`rotateY(180deg) translateZ(${half}px)`,
  Right:`rotateY(90deg) translateZ(${half}px)`,Left:`rotateY(-90deg) translateZ(${half}px)`,
  Top:`rotateX(90deg) translateZ(${half}px)`,Bottom:`rotateX(-90deg) translateZ(${half}px)`,
}[name]);

// ── Tile ───────────────────────────────────────────────────────────────────
const Tile = ({ clip, color, px, isSelected, isPlaying, onClick }) => {
  const dot = SENT[(clip?.sentiment||'').toLowerCase()] || SENT.neutral;
  if (!clip) return <Box sx={{ width:px,height:px,borderRadius:'5px',background:'rgba(0,0,0,.55)',border:'1px solid rgba(255,255,255,.05)',flexShrink:0 }} />;
  return (
    <Box onClick={()=>onClick(clip)} sx={{
      width:px,height:px,borderRadius:'6px',overflow:'hidden',position:'relative',
      flexShrink:0, background:'#0a0a12', cursor:'pointer',
      border: isPlaying?'2px solid #fff': isSelected?`2px solid ${color}`:'1px solid rgba(255,255,255,.15)',
      animation: isPlaying?`${ring} 1s ease-in-out infinite`:'none',
      boxShadow: isSelected&&!isPlaying?`0 0 8px ${color}88`:'none',
      transition:'border .15s',
      '&:hover':{ border:`2px solid ${color}`, zIndex:2 },
    }}>
      {clip.thumbnail && <Box component="img" src={clip.thumbnail}
        sx={{ position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',display:'block' }} />}
      <Box sx={{ position:'absolute',bottom:0,left:0,right:0,
        background:'linear-gradient(to top,rgba(0,0,0,.88) 0%,rgba(0,0,0,.5) 55%,transparent 100%)',
        p:`${Math.max(10,px*.18)}px 4px 3px` }}>
        <Typography sx={{ fontSize:`${Math.max(7,px*.1)}px`,color:'rgba(255,255,255,.9)',lineHeight:1.2,
          overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical' }}>
          {clip.sentence}
        </Typography>
      </Box>
      <Box sx={{ position:'absolute',top:3,left:4,width:5,height:5,borderRadius:'50%',background:dot }} />
      {isPlaying && <Box sx={{ position:'absolute',top:4,right:4,width:7,height:7,borderRadius:'50%',background:'#fff' }} />}
    </Box>
  );
};

// ── Face ───────────────────────────────────────────────────────────────────
const Face = ({ face, clips, n, px, selectedIds, playingId, onTileClick, isActive }) => {
  const tiles = Array.from({length:n*n},(_,i)=>clips[i]||null);
  return (
    <Box sx={{ position:'absolute',inset:0,background:'#0c0c18',
      border:`2px solid ${isActive?face.color:'rgba(255,255,255,.1)'}`,
      borderRadius:'6px',overflow:'hidden',
      backfaceVisibility:'hidden',WebkitBackfaceVisibility:'hidden',
      display:'flex',flexDirection:'column' }}>
      <Box sx={{ height:22,flexShrink:0,display:'flex',alignItems:'center',gap:1,px:1.5,
        background:isActive?`${face.color}28`:'rgba(255,255,255,.05)',
        borderBottom:`1px solid ${isActive?face.color+'44':'rgba(255,255,255,.08)'}` }}>
        <Box sx={{ width:6,height:6,borderRadius:'50%',background:face.color }} />
        <Typography sx={{ fontSize:'8px',fontWeight:700,letterSpacing:'2px',textTransform:'uppercase',
          color:isActive?face.color:'rgba(255,255,255,.35)' }}>
          {face.name} · {face.label}
        </Typography>
      </Box>
      <Box sx={{ flex:1,display:'grid',
        gridTemplateColumns:`repeat(${n},${px}px)`,gridTemplateRows:`repeat(${n},${px}px)`,
        gap:'3px',p:'4px',alignContent:'center',justifyContent:'center' }}>
        {tiles.map((clip,i)=>(
          <Tile key={clip?.id??`e${i}`} clip={clip} color={face.color} px={px}
            isSelected={clip&&selectedIds.has(clip.id)}
            isPlaying={clip&&clip.id===playingId}
            onClick={onTileClick} />
        ))}
      </Box>
    </Box>
  );
};

// ── Arrow button ───────────────────────────────────────────────────────────
const Arr = ({ label, onClick }) => (
  <Box onClick={onClick} sx={{
    width:38,height:38,borderRadius:'50%',cursor:'pointer',
    display:'flex',alignItems:'center',justifyContent:'center',
    background:'rgba(255,255,255,.08)',border:'1px solid rgba(255,255,255,.18)',
    fontSize:'15px',color:'rgba(255,255,255,.8)',userSelect:'none',
    transition:'all .15s','&:hover':{ background:'rgba(255,255,255,.2)',color:'#fff' },
  }}>{label}</Box>
);

// ── Main ───────────────────────────────────────────────────────────────────
const CubeView = ({ rows, videoFile, onOpenChessboard }) => {
  const clips = useMemo(()=>rows.filter(r=>!r.space),[rows]);

  const [rotX,setRotX]   = useState(-20);
  const [rotY,setRotY]   = useState(0);
  const [n,setN]         = useState(3);
  const [scale,setScale] = useState(1);
  const [activeFace,setActiveFace] = useState(0);
  const [animating,setAnimating]   = useState(false);
  const [selectedIds,setSelIds]    = useState(new Set());
  const [playingId,setPlayId]      = useState(null);
  const [preview,setPreview]       = useState(null);

  const dragRef  = useRef(null);
  const videoRef = useRef(null);
  const rafRef   = useRef(null);

  const BASE = 500;
  const cubeSize = Math.round(BASE * scale);
  const half     = cubeSize / 2;
  const tilePx   = Math.floor((cubeSize - 22 - (n-1)*3 - 8) / n);

  const faceClips = useMemo(()=>{
    const sel  = clips.filter(c=>selectedIds.has(c.id));
    const rest = clips.filter(c=>!selectedIds.has(c.id));
    const all  = [...sel,...rest];
    return FACES.map((_,i)=>all.slice(i*n*n,(i+1)*n*n));
  },[clips,n,selectedIds]);

  // ── Snap ──────────────────────────────────────────────────────────────
  const snap = useCallback((i)=>{
    setAnimating(true);
    setRotX(SNAPS[i].rx); setRotY(SNAPS[i].ry); setActiveFace(i);
    setTimeout(()=>setAnimating(false),550);
  },[]);

  const goLeft  = ()=>{ const c=Y_CYCLE.indexOf(activeFace); snap(Y_CYCLE[(c+1)%Y_CYCLE.length]); };
  const goRight = ()=>{ const c=Y_CYCLE.indexOf(activeFace); snap(Y_CYCLE[(c-1+Y_CYCLE.length)%Y_CYCLE.length]); };
  const goUp    = ()=>snap(4);
  const goDown  = ()=>snap(5);

  // ── Drag rotation ─────────────────────────────────────────────────────
  const onMD = e=>{ dragRef.current={x:e.clientX,y:e.clientY,rotX,rotY}; e.preventDefault(); };
  const onTS = e=>{ const t=e.touches[0]; dragRef.current={x:t.clientX,y:t.clientY,rotX,rotY}; };
  const onMM = useCallback(e=>{ if(!dragRef.current)return; setRotY(dragRef.current.rotY+(e.clientX-dragRef.current.x)*.5); setRotX(dragRef.current.rotX-(e.clientY-dragRef.current.y)*.5); },[]);
  const onTM = useCallback(e=>{ if(!dragRef.current)return; const t=e.touches[0]; setRotY(dragRef.current.rotY+(t.clientX-dragRef.current.x)*.5); setRotX(dragRef.current.rotX-(t.clientY-dragRef.current.y)*.5); },[]);
  const onUp = ()=>{ dragRef.current=null; };

  useEffect(()=>{
    window.addEventListener('mousemove',onMM); window.addEventListener('mouseup',onUp);
    window.addEventListener('touchmove',onTM,{passive:true}); window.addEventListener('touchend',onUp);
    return ()=>{ window.removeEventListener('mousemove',onMM); window.removeEventListener('mouseup',onUp);
      window.removeEventListener('touchmove',onTM); window.removeEventListener('touchend',onUp); };
  },[onMM,onTM]);

  // ── Play a single clip ─────────────────────────────────────────────────
  // This is the same simple pattern as the live site
  const doPlayClip = useCallback((clip)=>{
    const vid = videoRef.current;
    if(!vid||!videoFile||!clip)return;
    if(rafRef.current)cancelAnimationFrame(rafRef.current);
    setPreview(clip); setPlayId(clip.id);
    vid.src = videoFile;
    vid.currentTime = clip.start_in??0;
    vid.addEventListener('play',()=>{
      const endIn = clip.end_in??((clip.start_in??0)+30);
      if(rafRef.current)cancelAnimationFrame(rafRef.current);
      const check=()=>{ if(vid.currentTime>=endIn){vid.pause();setPlayId(null);} else rafRef.current=requestAnimationFrame(check); };
      rafRef.current=requestAnimationFrame(check);
    },{once:true});
    vid.play().catch(()=>{});
  },[]);

  const handleTile = useCallback((clip)=>{
    setSelIds(prev=>{ const n=new Set(prev); if(n.has(clip.id))n.delete(clip.id); else n.add(clip.id); return n; });
    doPlayClip(clip);
  },[doPlayClip]);

  // ── Play whole face ────────────────────────────────────────────────────
  const qRef=useRef([]); const qIdx=useRef(0);
  const playNextQ=useCallback(()=>{
    const vid=videoRef.current;
    if(!vid||qIdx.current>=qRef.current.length){setPlayId(null);return;}
    const clip=qRef.current[qIdx.current];
    setPreview(clip); setPlayId(clip.id);
    vid.src=videoFile; vid.currentTime=clip.start_in??0;
    const endIn=clip.end_in??((clip.start_in??0)+30);
    vid.addEventListener('play',()=>{
      if(rafRef.current)cancelAnimationFrame(rafRef.current);
      const check=()=>{ if(vid.currentTime>=endIn){qIdx.current++;playNextQ();} else rafRef.current=requestAnimationFrame(check); };
      rafRef.current=requestAnimationFrame(check);
    },{once:true});
    vid.play().catch(()=>{});
  },[]);

  const playFace=()=>{ if(rafRef.current)cancelAnimationFrame(rafRef.current); qRef.current=faceClips[activeFace].filter(Boolean); qIdx.current=0; playNextQ(); };
  const stop=()=>{ if(rafRef.current)cancelAnimationFrame(rafRef.current); videoRef.current?.pause(); setPlayId(null); };
  useEffect(()=>()=>{if(rafRef.current)cancelAnimationFrame(rafRef.current);},[]);

  const isDragging = !!dragRef.current;

  return (
    <Box sx={{ display:'flex',height:'100%',overflow:'hidden',
      background:'radial-gradient(ellipse 60% 60% at 50% 40%,rgba(230,29,80,.07) 0%,transparent 60%),#07070a' }}>

      {/* ── Left panel ──────────────────────────────────────────────── */}
      <Box sx={{ width:175,flexShrink:0,p:1.5,display:'flex',flexDirection:'column',gap:2,
        borderRight:'1px solid rgba(255,255,255,.07)',background:'rgba(8,8,14,.9)',overflow:'auto' }}>

        <Box>
          <Typography sx={{ fontSize:'8px',fontWeight:700,letterSpacing:'3px',textTransform:'uppercase',color:'rgba(255,255,255,.2)',mb:1 }}>
            Tiles / Face
          </Typography>
          <Box sx={{ display:'flex',gap:'5px',flexWrap:'wrap' }}>
            {[2,3,4,5].map(v=>(
              <Box key={v} onClick={()=>setN(v)} sx={{ px:1.5,py:.5,borderRadius:'6px',cursor:'pointer',
                fontSize:'11px',fontWeight:700,
                background:n===v?'rgba(230,29,80,.2)':'rgba(255,255,255,.05)',
                border:n===v?'1px solid rgba(230,29,80,.5)':'1px solid rgba(255,255,255,.1)',
                color:n===v?'#E61D50':'rgba(255,255,255,.4)' }}>{v}×{v}</Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize:'8px',fontWeight:700,letterSpacing:'3px',textTransform:'uppercase',color:'rgba(255,255,255,.2)',mb:1 }}>
            Cube Size
          </Typography>
          <Box sx={{ display:'flex',gap:'5px',flexWrap:'wrap' }}>
            {[{l:'S',v:.7},{l:'M',v:1},{l:'L',v:1.35},{l:'XL',v:1.7}].map(({l,v})=>(
              <Box key={l} onClick={()=>setScale(v)} sx={{ px:1.5,py:.5,borderRadius:'6px',cursor:'pointer',
                fontSize:'11px',fontWeight:700,
                background:scale===v?'rgba(129,140,248,.2)':'rgba(255,255,255,.05)',
                border:scale===v?'1px solid rgba(129,140,248,.5)':'1px solid rgba(255,255,255,.1)',
                color:scale===v?'#818cf8':'rgba(255,255,255,.4)' }}>{l}</Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontSize:'8px',fontWeight:700,letterSpacing:'3px',textTransform:'uppercase',color:'rgba(255,255,255,.2)',mb:1 }}>Faces</Typography>
          {FACES.map((f,i)=>(
            <Box key={f.name} onClick={()=>snap(i)} sx={{ display:'flex',alignItems:'center',gap:1,py:.6,px:1,
              borderRadius:'7px',cursor:'pointer',mb:'3px',
              background:activeFace===i?`${f.color}18`:'transparent',
              border:activeFace===i?`1px solid ${f.color}44`:'1px solid transparent',
              '&:hover':{background:`${f.color}10`} }}>
              <Box sx={{ width:8,height:8,borderRadius:'2px',background:f.color,flexShrink:0 }} />
              <Typography sx={{ fontSize:'10px',flex:1,fontWeight:activeFace===i?700:400,
                color:activeFace===i?f.color:'rgba(255,255,255,.5)' }}>{f.name}</Typography>
              <Typography sx={{ fontSize:'9px',color:'rgba(255,255,255,.2)' }}>{faceClips[i].filter(Boolean).length}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ display:'flex',flexDirection:'column',gap:'6px' }}>
          <Box onClick={playFace} sx={{ py:.9,borderRadius:'7px',cursor:'pointer',textAlign:'center',
            border:`1px solid ${FACES[activeFace].color}44`,color:FACES[activeFace].color,
            fontSize:'11px',fontWeight:600,background:`${FACES[activeFace].color}12`,
            '&:hover':{background:`${FACES[activeFace].color}22`} }}>
            ▶ Play {FACES[activeFace].name}
          </Box>
          <Box onClick={stop} sx={{ py:.9,borderRadius:'7px',cursor:'pointer',textAlign:'center',
            border:'1px solid rgba(255,255,255,.1)',color:'rgba(255,255,255,.4)',fontSize:'11px',
            background:'rgba(255,255,255,.04)','&:hover':{background:'rgba(255,255,255,.08)'} }}>■ Stop</Box>
          {selectedIds.size>0 && (
            <Box onClick={()=>setActiveFace(0)} sx={{ py:.9,borderRadius:'7px',cursor:'pointer',textAlign:'center',
              border:'1px solid rgba(230,29,80,.4)',color:'#E61D50',fontSize:'11px',fontWeight:700,
              background:'rgba(230,29,80,.1)','&:hover':{background:'rgba(230,29,80,.18)'} }}>
              📌 Pin {selectedIds.size} to Front
            </Box>
          )}
          {selectedIds.size>0 && <Box onClick={()=>setSelIds(new Set())} sx={{ py:.5,cursor:'pointer',textAlign:'center',color:'rgba(255,255,255,.2)',fontSize:'10px','&:hover':{color:'rgba(255,255,255,.4)'}  }}>Clear selection</Box>}
        </Box>

        {/* Open Chessboard */}
        {onOpenChessboard && (
          <Box onClick={onOpenChessboard} sx={{ mt:'auto',py:1,borderRadius:'8px',cursor:'pointer',textAlign:'center',
            border:'1px solid rgba(200,80,60,.35)',color:'#c8503a',fontSize:'11px',fontWeight:700,
            background:'rgba(200,80,60,.08)','&:hover':{background:'rgba(200,80,60,.16)'} }}>
            ♟ Open Chessboard
          </Box>
        )}
      </Box>

      {/* ── Cube scene ────────────────────────────────────────────────── */}
      <Box sx={{ flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',overflow:'hidden',gap:1.5 }}>
        <Typography sx={{ fontSize:'9px',color:'rgba(255,255,255,.2)',letterSpacing:'2px' }}>
          DRAG TO SPIN · CLICK TILES TO PLAY & SELECT
        </Typography>

        <Box sx={{ width:cubeSize,height:cubeSize,perspective:`${cubeSize*2.2}px`,
          cursor:'grab',userSelect:'none',
          animation:`${float} 7s ease-in-out infinite`,
          '&:active':{cursor:'grabbing'} }}
          onMouseDown={onMD} onTouchStart={onTS}>
          <Box sx={{ width:'100%',height:'100%',position:'relative',transformStyle:'preserve-3d',
            transform:`rotateX(${rotX}deg) rotateY(${rotY}deg)`,
            transition:isDragging||animating?'none':'transform .55s cubic-bezier(.4,0,.2,1)' }}>
            {FACES.map((f,i)=>(
              <Box key={f.name} sx={{ position:'absolute',width:'100%',height:'100%',transform:faceXf(f.name,half) }}>
                <Face face={f} clips={faceClips[i]} n={n} px={tilePx}
                  selectedIds={selectedIds} playingId={playingId}
                  onTileClick={handleTile} isActive={activeFace===i} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Arrow controls */}
        <Box sx={{ display:'flex',alignItems:'center',gap:1.5 }}>
          <Arr label="↑" onClick={goUp} />
          <Box sx={{ display:'flex',gap:1,alignItems:'center' }}>
            <Arr label="←" onClick={goLeft} />
            <Box sx={{ display:'flex',gap:'5px',alignItems:'center',px:1 }}>
              {FACES.map((f,i)=>(
                <Box key={f.name} onClick={()=>snap(i)} sx={{ height:8,width:activeFace===i?20:8,borderRadius:'4px',
                  background:activeFace===i?f.color:'rgba(255,255,255,.2)',cursor:'pointer',transition:'all .3s',
                  boxShadow:activeFace===i?`0 0 8px ${f.color}`:'none' }} />
              ))}
            </Box>
            <Arr label="→" onClick={goRight} />
          </Box>
          <Arr label="↓" onClick={goDown} />
        </Box>
        <Typography sx={{ fontSize:'9px',color:'rgba(255,255,255,.12)' }}>← → sides · ↑↓ top/bottom</Typography>
      </Box>

      {/* ── Player ─────────────────────────────────────────────────────── */}
      <Box sx={{ width:210,flexShrink:0,borderLeft:'1px solid rgba(255,255,255,.07)',
        display:'flex',flexDirection:'column',p:1.5,gap:1.5,background:'rgba(8,8,14,.9)' }}>
        <Typography sx={{ fontSize:'8px',fontWeight:700,letterSpacing:'3px',textTransform:'uppercase',color:'rgba(255,255,255,.2)' }}>Now Playing</Typography>
        <video ref={videoRef} controls style={{ width:'100%',borderRadius:'8px',background:'#000',aspectRatio:'16/9',objectFit:'cover' }} />
        {preview ? (
          <Box>
            <Typography sx={{ fontSize:'10px',fontWeight:600,color:'#fff',lineHeight:1.4,mb:.5 }}>{preview.sentence}</Typography>
            <Typography sx={{ fontSize:'9px',color:'rgba(255,255,255,.4)' }}>{preview.speaker}</Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize:'10px',color:'rgba(255,255,255,.2)',fontStyle:'italic' }}>Click any tile to play it</Typography>
        )}
      </Box>
    </Box>
  );
};

export default CubeView;
